"use strict";
/**
 * @streamlain/field-types — sl-v6fl.1
 *
 * Single source of truth for field-type normalization and compatibility.
 *
 * Background:
 *   Two type "worlds" used to live side-by-side:
 *     - Frontend `field.type`:  'text' | 'rich-text' | 'attachment' | …
 *     - Runner   `field.dataType`: 'string' | 'file' | …
 *
 *   The runner's WorkflowInputBinder dispatched on `dataType` while the
 *   seed templates wrote `type`, so file inputs silently fell through
 *   to `coerceJson` and the AI step received `attachmentCount: 0`.
 *
 *   This package owns the canonical taxonomy and the pure functions
 *   that map any incoming type string into it. The frontend editor and
 *   the backend binder MUST go through here so they always agree.
 *
 * Pure TypeScript, zero runtime dependencies — safe for both
 * frontend bundles and backend NestJS modules.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.RUNNER_LOST_ERROR = exports.ROW_LIST_FIELD_TYPE = exports.findRowListField = exports.WorkflowNumberInputError = exports.parseWorkflowNumber = exports.decodeNumberInputError = exports.encodeCellValueError = exports.decodeCellValueError = exports.CELL_VALUE_ERROR_COLUMN_TYPES = exports.WORKFLOW_PREVIEW_SAFE_STEP_TYPES = exports.STEP_TYPES = exports.LOOP_PORTS = exports.LOOP_EXIT_PORT = exports.LOOP_CONTINUE_PORT = exports.isWorkflowPreviewSafeStepType = exports.isValidStepType = exports.runnerProfileSatisfies = exports.RunnerProfileResolutionError = exports.RUNNER_PROFILES = exports.RUNNER_PROFILE_HEAP_MIB = exports.RUNNER_PROFILE_CAPABILITIES = exports.resolveRunnerRequirements = exports.resolveRunnerProfileForStep = exports.parseRunnerProfile = exports.parseRunnerCapabilities = exports.MEDIA_NODE_PROFILE_BY_SLUG = exports.joinRunnerProfiles = exports.withAutomationTargetContext = exports.readAutomationOutputField = exports.mapAutomationTargetValues = exports.automationTargetAlias = exports.AutomationOutputFieldMissingError = exports.automationMatchKeyValues = exports.automationFanoutData = exports.automationCellValue = exports.AUTOMATION_TARGET_CONTEXT_FIELD = exports.WEBHOOK_WHOLE_PAYLOAD_COLUMN_ID = exports.WEBHOOK_PAYLOAD_TABLE_ID = exports.selectWebhookPayloadMappings = exports.RELATION_ROWS_COLUMN_ID = exports.RELATION_MAPPING_ERROR = exports.isOptionsInputMapping = exports.INPUT_MAPPING_OPTIONS_SOURCE = exports.hasValidRelationInputMappings = exports.applyWebhookPayloadMappings = exports.automationSelectInput = exports.automationMultiSelectInput = exports.rateTableMatch = exports.rateFieldMatch = exports.autoMatchFields = void 0;
exports.isBlobReference = isBlobReference;
exports.normalizeType = normalizeType;
exports.checkCompatibility = checkCompatibility;
const row_list_1 = require("./row-list");
// sl-8zjk: StepType + STEP_TYPES + isValidStepType + LOOP port constants
// used to live in three "byte-compatible" mirrors plus two drift-prone
// fallbacks. Canonical definition now lives in ./step-type — runner,
// server, and frontend all re-export from here.
var auto_match_fields_1 = require("./auto-match-fields");
Object.defineProperty(exports, "autoMatchFields", { enumerable: true, get: function () { return auto_match_fields_1.autoMatchFields; } });
Object.defineProperty(exports, "rateFieldMatch", { enumerable: true, get: function () { return auto_match_fields_1.rateFieldMatch; } });
Object.defineProperty(exports, "rateTableMatch", { enumerable: true, get: function () { return auto_match_fields_1.rateTableMatch; } });
var input_cell_1 = require("./input-cell");
Object.defineProperty(exports, "automationMultiSelectInput", { enumerable: true, get: function () { return input_cell_1.automationMultiSelectInput; } });
Object.defineProperty(exports, "automationSelectInput", { enumerable: true, get: function () { return input_cell_1.automationSelectInput; } });
var input_mapping_1 = require("./input-mapping");
Object.defineProperty(exports, "applyWebhookPayloadMappings", { enumerable: true, get: function () { return input_mapping_1.applyWebhookPayloadMappings; } });
Object.defineProperty(exports, "hasValidRelationInputMappings", { enumerable: true, get: function () { return input_mapping_1.hasValidRelationInputMappings; } });
Object.defineProperty(exports, "INPUT_MAPPING_OPTIONS_SOURCE", { enumerable: true, get: function () { return input_mapping_1.INPUT_MAPPING_OPTIONS_SOURCE; } });
Object.defineProperty(exports, "isOptionsInputMapping", { enumerable: true, get: function () { return input_mapping_1.isOptionsInputMapping; } });
Object.defineProperty(exports, "RELATION_MAPPING_ERROR", { enumerable: true, get: function () { return input_mapping_1.RELATION_MAPPING_ERROR; } });
Object.defineProperty(exports, "RELATION_ROWS_COLUMN_ID", { enumerable: true, get: function () { return input_mapping_1.RELATION_ROWS_COLUMN_ID; } });
Object.defineProperty(exports, "selectWebhookPayloadMappings", { enumerable: true, get: function () { return input_mapping_1.selectWebhookPayloadMappings; } });
Object.defineProperty(exports, "WEBHOOK_PAYLOAD_TABLE_ID", { enumerable: true, get: function () { return input_mapping_1.WEBHOOK_PAYLOAD_TABLE_ID; } });
Object.defineProperty(exports, "WEBHOOK_WHOLE_PAYLOAD_COLUMN_ID", { enumerable: true, get: function () { return input_mapping_1.WEBHOOK_WHOLE_PAYLOAD_COLUMN_ID; } });
var output_mapping_1 = require("./output-mapping");
Object.defineProperty(exports, "AUTOMATION_TARGET_CONTEXT_FIELD", { enumerable: true, get: function () { return output_mapping_1.AUTOMATION_TARGET_CONTEXT_FIELD; } });
Object.defineProperty(exports, "automationCellValue", { enumerable: true, get: function () { return output_mapping_1.automationCellValue; } });
Object.defineProperty(exports, "automationFanoutData", { enumerable: true, get: function () { return output_mapping_1.automationFanoutData; } });
Object.defineProperty(exports, "automationMatchKeyValues", { enumerable: true, get: function () { return output_mapping_1.automationMatchKeyValues; } });
Object.defineProperty(exports, "AutomationOutputFieldMissingError", { enumerable: true, get: function () { return output_mapping_1.AutomationOutputFieldMissingError; } });
Object.defineProperty(exports, "automationTargetAlias", { enumerable: true, get: function () { return output_mapping_1.automationTargetAlias; } });
Object.defineProperty(exports, "mapAutomationTargetValues", { enumerable: true, get: function () { return output_mapping_1.mapAutomationTargetValues; } });
Object.defineProperty(exports, "readAutomationOutputField", { enumerable: true, get: function () { return output_mapping_1.readAutomationOutputField; } });
Object.defineProperty(exports, "withAutomationTargetContext", { enumerable: true, get: function () { return output_mapping_1.withAutomationTargetContext; } });
var runner_profile_1 = require("./runner-profile");
Object.defineProperty(exports, "joinRunnerProfiles", { enumerable: true, get: function () { return runner_profile_1.joinRunnerProfiles; } });
Object.defineProperty(exports, "MEDIA_NODE_PROFILE_BY_SLUG", { enumerable: true, get: function () { return runner_profile_1.MEDIA_NODE_PROFILE_BY_SLUG; } });
Object.defineProperty(exports, "parseRunnerCapabilities", { enumerable: true, get: function () { return runner_profile_1.parseRunnerCapabilities; } });
Object.defineProperty(exports, "parseRunnerProfile", { enumerable: true, get: function () { return runner_profile_1.parseRunnerProfile; } });
Object.defineProperty(exports, "resolveRunnerProfileForStep", { enumerable: true, get: function () { return runner_profile_1.resolveRunnerProfileForStep; } });
Object.defineProperty(exports, "resolveRunnerRequirements", { enumerable: true, get: function () { return runner_profile_1.resolveRunnerRequirements; } });
Object.defineProperty(exports, "RUNNER_PROFILE_CAPABILITIES", { enumerable: true, get: function () { return runner_profile_1.RUNNER_PROFILE_CAPABILITIES; } });
Object.defineProperty(exports, "RUNNER_PROFILE_HEAP_MIB", { enumerable: true, get: function () { return runner_profile_1.RUNNER_PROFILE_HEAP_MIB; } });
Object.defineProperty(exports, "RUNNER_PROFILES", { enumerable: true, get: function () { return runner_profile_1.RUNNER_PROFILES; } });
Object.defineProperty(exports, "RunnerProfileResolutionError", { enumerable: true, get: function () { return runner_profile_1.RunnerProfileResolutionError; } });
Object.defineProperty(exports, "runnerProfileSatisfies", { enumerable: true, get: function () { return runner_profile_1.runnerProfileSatisfies; } });
var step_type_1 = require("./step-type");
Object.defineProperty(exports, "isValidStepType", { enumerable: true, get: function () { return step_type_1.isValidStepType; } });
Object.defineProperty(exports, "isWorkflowPreviewSafeStepType", { enumerable: true, get: function () { return step_type_1.isWorkflowPreviewSafeStepType; } });
Object.defineProperty(exports, "LOOP_CONTINUE_PORT", { enumerable: true, get: function () { return step_type_1.LOOP_CONTINUE_PORT; } });
Object.defineProperty(exports, "LOOP_EXIT_PORT", { enumerable: true, get: function () { return step_type_1.LOOP_EXIT_PORT; } });
Object.defineProperty(exports, "LOOP_PORTS", { enumerable: true, get: function () { return step_type_1.LOOP_PORTS; } });
Object.defineProperty(exports, "STEP_TYPES", { enumerable: true, get: function () { return step_type_1.STEP_TYPES; } });
Object.defineProperty(exports, "WORKFLOW_PREVIEW_SAFE_STEP_TYPES", { enumerable: true, get: function () { return step_type_1.WORKFLOW_PREVIEW_SAFE_STEP_TYPES; } });
const BLOB_REFERENCE_KEYS = new Set([
    'kind',
    'blobId',
    'filename',
    'mimeType',
    'size',
]);
/**
 * Type guard for `BlobReference`. Accepts `unknown` for safe narrowing
 * from untyped sources (row.data, HTTP response bodies, log payloads).
 */
function isBlobReference(value) {
    if (value === null || typeof value !== 'object')
        return false;
    const obj = value;
    const keys = Object.keys(obj);
    return (keys.length === BLOB_REFERENCE_KEYS.size &&
        keys.every(key => BLOB_REFERENCE_KEYS.has(key)) &&
        obj['kind'] === 'blob' &&
        typeof obj['blobId'] === 'string' &&
        typeof obj['filename'] === 'string' &&
        typeof obj['mimeType'] === 'string' &&
        typeof obj['size'] === 'number');
}
/**
 * Normalize a field/column type string to a canonical form for comparison.
 * Handles common type aliases and variations across different schema sources.
 *
 * Canonical values returned:
 *   'text', 'rich-text', 'number', 'checkbox', 'multi-select', 'select',
 *   'date', 'attachment', 'relation', 'row-list', or the lowercased input
 *   as-is for unknown values (e.g. 'json', 'boolean', 'array').
 */
function normalizeType(type) {
    const t = type.toLowerCase();
    if (t === row_list_1.ROW_LIST_FIELD_TYPE)
        return row_list_1.ROW_LIST_FIELD_TYPE;
    if (t.includes('text') || t.includes('title') || t === 'string')
        return 'text';
    if (t.includes('rich'))
        return 'rich-text';
    if (t.includes('number') || t.includes('progress'))
        return 'number';
    if (t.includes('check') || t === 'boolean' || t === 'bool')
        return 'checkbox';
    if (t.includes('multi'))
        return 'multi-select';
    if (t.includes('select'))
        return 'select';
    if (t.includes('date') || t.includes('time'))
        return 'date';
    if (t.includes('attach') || t.includes('file') || t.includes('image'))
        return 'attachment';
    if (t.includes('relation') || t.includes('link'))
        return 'relation';
    return t;
}
/**
 * Check type compatibility between a target field and a source field.
 *
 * Returns:
 * - 'compatible': types match exactly after normalization
 * - 'castable': automatic type conversion is possible
 * - 'incompatible': types cannot be safely converted
 */
function checkCompatibility(targetType, sourceType) {
    const tType = normalizeType(targetType);
    const sType = normalizeType(sourceType);
    if (tType === sType)
        return 'compatible';
    // A row list holds the rows of a run; no column fills it, it fills none.
    if (tType === row_list_1.ROW_LIST_FIELD_TYPE || sType === row_list_1.ROW_LIST_FIELD_TYPE)
        return 'incompatible';
    // Text/rich-text accepts almost anything
    if (tType === 'text' || tType === 'rich-text')
        return 'castable';
    // JSON accepts everything (serializable)
    if (tType === 'json')
        return 'castable';
    // Number can accept text (if parseable)
    if (tType === 'number' && sType === 'text')
        return 'castable';
    // Text source can feed most targets (with casting)
    if (sType === 'text')
        return 'castable';
    // JSON source can feed most targets (with extraction)
    if (sType === 'json')
        return 'castable';
    // Select/multi-select are interchangeable with text
    if ((tType === 'select' || tType === 'multi-select') &&
        (sType === 'text' || sType === 'select' || sType === 'multi-select'))
        return 'castable';
    return 'incompatible';
}
var cell_value_error_1 = require("./cell-value-error");
Object.defineProperty(exports, "CELL_VALUE_ERROR_COLUMN_TYPES", { enumerable: true, get: function () { return cell_value_error_1.CELL_VALUE_ERROR_COLUMN_TYPES; } });
Object.defineProperty(exports, "decodeCellValueError", { enumerable: true, get: function () { return cell_value_error_1.decodeCellValueError; } });
Object.defineProperty(exports, "encodeCellValueError", { enumerable: true, get: function () { return cell_value_error_1.encodeCellValueError; } });
var number_input_1 = require("./number-input");
Object.defineProperty(exports, "decodeNumberInputError", { enumerable: true, get: function () { return number_input_1.decodeNumberInputError; } });
Object.defineProperty(exports, "parseWorkflowNumber", { enumerable: true, get: function () { return number_input_1.parseWorkflowNumber; } });
Object.defineProperty(exports, "WorkflowNumberInputError", { enumerable: true, get: function () { return number_input_1.WorkflowNumberInputError; } });
var row_list_2 = require("./row-list");
Object.defineProperty(exports, "findRowListField", { enumerable: true, get: function () { return row_list_2.findRowListField; } });
Object.defineProperty(exports, "ROW_LIST_FIELD_TYPE", { enumerable: true, get: function () { return row_list_2.ROW_LIST_FIELD_TYPE; } });
var runner_lost_error_1 = require("./runner-lost-error");
Object.defineProperty(exports, "RUNNER_LOST_ERROR", { enumerable: true, get: function () { return runner_lost_error_1.RUNNER_LOST_ERROR; } });
